import React from 'react'
import PublicLayout from '@/pages/PublicLayout';
import NavigationBar from '@/components/pages/Landing/components/NavigationBar';
import { MODE_DARK, MODE_LIGHT } from '@/constants/theme.constant';
import useDarkMode from '@/utils/hooks/useDarkMode';
import LandingContainer from '@/components/pages/Landing/components/LandingContainer';
import { Head } from '@inertiajs/react';

const Privacy = () => {
  const [isDark, setMode] = useDarkMode()

  const mode = isDark ? MODE_DARK : MODE_LIGHT

  const toggleMode = () => {
    setMode(mode === MODE_LIGHT ? MODE_DARK : MODE_LIGHT)
  }

  return (
    <>
      <Head>
        <title>Privacy Policy</title>
        <meta name="description" content="Learn how MemoSlate collects, uses, and protects your personal information. Read our comprehensive privacy policy and understand your data rights." />
      </Head>

      <main className="sm:px-4 lg:px-0 text-base">
        <NavigationBar toggleMode={toggleMode} mode={mode} />
        <LandingContainer className="relative mt-36">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-8 md:p-12 lg:p-16">
            <h1 className="text-4xl font-bold text-gray-900 dark:text-gray-100 mb-4">
              Privacy Policy
            </h1>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-8">
              Last Updated: July 3, 2026
            </p>

            <div className="prose prose-gray dark:prose-invert max-w-none">
              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
                  1. Introduction
                </h2>
                <p className="text-gray-700 dark:text-gray-300 mb-4">
                  Welcome to MemoSlate ("we", "us", or "our"). We are committed to protecting your personal
                  information and your right to privacy. This Privacy Policy explains how we collect, use,
                  disclose, and safeguard your information when you use MemoSlate, an early-access personal
                  notes workspace, and the website at memoslate.com (collectively, the "Service").
                </p>
                <p className="text-gray-700 dark:text-gray-300 mb-4">
                  By using the Service, you agree to the collection and use of information in accordance with
                  this Privacy Policy. If you do not agree with our policies and practices, please do not use
                  the Service.
                </p>
                <p className="text-gray-700 dark:text-gray-300 mb-4">
                  Because MemoSlate is in early access, the Service may change over time. We may update how
                  data is processed as features are added, changed, limited, or removed. We will update this
                  Privacy Policy when material changes are made.
                </p>
                <p className="text-gray-700 dark:text-gray-300">
                  This Privacy Policy is operated by Themety, located in Vilnius, Lithuania.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
                  2. Information We Collect
                </h2>
                <p className="text-gray-700 dark:text-gray-300 mb-4">
                  We collect information that you provide directly to us, information we obtain automatically
                  when you use our Service, and information from third-party sources.
                </p>

                <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-3 mt-6">
                  2.1 Information You Provide to Us
                </h3>
                <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 mb-4 space-y-2">
                  <li><strong>Account Information:</strong> When you create an account, we collect your email
                  address, name (or username), and password.</li>
                  <li><strong>Note Content:</strong> We store the notes, checklists, and related content you
                  create using the Service. Regular note content is stored on our servers so the Service can
                  display, sync, and manage your notes. Certain sensitive snippets may be stored in secret
                  fields that use client-side encryption before they are sent to our servers. Secret field
                  labels, metadata, timestamps, note structure, and other technical information may not be
                  encrypted end-to-end.</li>
                  <li><strong>Communications:</strong> If you contact us directly, we may receive additional
                  information about you such as the contents of your message.</li>
                </ul>

                <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-3 mt-6">
                  2.2 Information Collected Automatically
                </h3>
                <p className="text-gray-700 dark:text-gray-300 mb-4">
                  When you access or use our Service, we automatically collect certain information:
                </p>
                <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 mb-4 space-y-2">
                  <li><strong>Device Information:</strong> We collect information about the device you use to
                  access the Service, including device type, operating system, and browser type.</li>
                  <li><strong>IP Address:</strong> We automatically collect your IP address when you use our Service.</li>
                  <li><strong>Usage Analytics:</strong> We may use analytics tools, such as Google Analytics if
                  enabled, to understand how users interact with the Service, including pages visited, features
                  used, time spent, and navigation paths.</li>
                  <li><strong>Internal Usage Events:</strong> We may also collect internal usage events, such as
                  page views, feature usage, and technical events, to maintain, secure, and improve the Service.</li>
                  <li><strong>Log Data:</strong> Our servers automatically record information including your IP address,
                  browser type, referring/exit pages, and timestamps.</li>
                </ul>

                <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-3 mt-6">
                  2.3 Cookies and Tracking Technologies
                </h3>
                <p className="text-gray-700 dark:text-gray-300 mb-4">
                  We use cookies and similar tracking technologies to track activity on our Service:
                </p>
                <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 mb-4 space-y-2">
                  <li><strong>Session Cookies:</strong> Essential cookies that provide login, session, and
                  security functionality.</li>
                  <li><strong>Analytics Cookies:</strong> We may use analytics cookies or similar technologies
                  where enabled and, where required, subject to consent.</li>
                  <li><strong>Security Cookies:</strong> Used by Google reCAPTCHA to protect against spam and abuse.</li>
                </ul>
                <p className="text-gray-700 dark:text-gray-300">
                  You can instruct your browser to refuse all cookies or to indicate when a cookie is being sent.
                  However, if you do not accept cookies, you may not be able to use some portions of our Service.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
                  3. How We Use Your Information
                </h2>
                <p className="text-gray-700 dark:text-gray-300 mb-4">
                  We use the information we collect for various purposes:
                </p>
                <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 mb-4 space-y-2">
                  <li>To provide and maintain the Service during early access</li>
                  <li>To create and manage your account</li>
                  <li>To store, sync, and display your notes and related content across devices</li>
                  <li>To provide secret field functionality</li>
                  <li>To authenticate your identity and prevent fraud</li>
                  <li>To apply account, storage, note history, usage, or security limits</li>
                  <li>To send you technical notices, updates, security alerts, and support messages</li>
                  <li>To send you marketing communications about new features, products, or services (with your consent)</li>
                  <li>To respond to your comments, questions, and customer service requests</li>
                  <li>To detect abuse, troubleshoot errors, and improve reliability</li>
                  <li>To analyze usage patterns and improve the product</li>
                  <li>To comply with legal obligations and enforce our Terms and Conditions</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
                  4. Data Security and Encryption
                </h2>
                <p className="text-gray-700 dark:text-gray-300 mb-4">
                  We take the security of your data seriously and implement various measures to protect your information:
                </p>
                <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 mb-4 space-y-2">
                  <li><strong>Transport Security:</strong> Data transmitted between your device and our servers
                  is encrypted using SSL/TLS protocols.</li>
                  <li><strong>Secret Fields:</strong> Certain sensitive snippets may be stored in secret fields.
                  Secret field values are encrypted on your device before they are sent to our servers. When
                  this feature is used correctly, we do not have access to the decrypted secret field values.</li>
                  <li><strong>Regular Note Content:</strong> Regular note content, note metadata, account data,
                  logs, and technical information are not necessarily end-to-end encrypted and may be processed
                  by us as necessary to provide, secure, maintain, and improve the Service.</li>
                  <li><strong>Server Storage:</strong> Notes and related data are stored on servers located in Germany.</li>
                  <li><strong>Access Controls:</strong> We implement strict access controls to limit who can access
                  your data within our organization.</li>
                </ul>
                <p className="text-gray-700 dark:text-gray-300">
                  However, please note that no method of transmission over the internet or electronic storage is
                  100% secure. While we strive to use commercially acceptable means to protect your personal information,
                  we cannot guarantee its absolute security.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
                  5. Third-Party Services
                </h2>
                <p className="text-gray-700 dark:text-gray-300 mb-4">
                  We use the following third-party services that may collect information about you:
                </p>
                <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 mb-4 space-y-2">
                  <li><strong>Google Analytics:</strong> We may use Google Analytics, if enabled, to analyze how
                  users interact with our Service. Google Analytics collects information such as how often users
                  visit our Service, what pages they visit, and what other sites they used prior to coming to
                  our Service. You can opt-out of Google Analytics by installing the Google Analytics opt-out
                  browser add-on.</li>
                  <li><strong>Google reCAPTCHA:</strong> We use reCAPTCHA to protect our Service from spam and abuse.
                  reCAPTCHA collects hardware and software information and sends it to Google for analysis. The
                  information collected is subject to Google's Privacy Policy.</li>
                  <li><strong>Error Monitoring:</strong> We may use Sentry or similar error monitoring tools to
                  detect, diagnose, and fix technical errors. These tools may collect technical information such
                  as browser details, device information, error stack traces, page URL, user/session identifiers,
                  and related diagnostic context. We do not intentionally send note content or secret field values
                  to error monitoring tools.</li>
                  <li><strong>Payment Processors:</strong> If we introduce paid features in the future, we may use
                  third-party payment processors to handle payments. We do not store your payment card information
                  on our servers.</li>
                </ul>
                <p className="text-gray-700 dark:text-gray-300">
                  These third-party services have their own privacy policies addressing how they use such information.
                  We encourage you to review their privacy policies.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
                  6. Data Sharing and Disclosure
                </h2>
                <p className="text-gray-700 dark:text-gray-300 mb-4">
                  We do not sell, trade, or rent your personal information to third parties. We may share your
                  information only in the following circumstances:
                </p>
                <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 mb-4 space-y-2">
                  <li><strong>With Your Consent:</strong> We may share your information when you give us explicit
                  permission to do so.</li>
                  <li><strong>Service Providers:</strong> We may share your information with third-party service
                  providers who perform services on our behalf, such as hosting providers, analytics providers
                  (if used), error monitoring providers (if used), transactional email providers, and payment
                  processors (if introduced later).</li>
                  <li><strong>Legal Requirements:</strong> We may disclose your information if required to do so by
                  law or in response to valid requests by public authorities (e.g., court orders, subpoenas).</li>
                  <li><strong>Protection of Rights:</strong> We may disclose your information when we believe it is
                  necessary to protect our rights, protect your safety or the safety of others, investigate fraud,
                  or respond to a government request.</li>
                  <li><strong>Business Transfers:</strong> If we are involved in a merger, acquisition, or sale of
                  assets, your information may be transferred as part of that transaction.</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
                  7. Data Retention
                </h2>
                <p className="text-gray-700 dark:text-gray-300 mb-4">
                  We retain your personal information for as long as necessary to provide you with our Service and
                  for legitimate business purposes:
                </p>
                <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 mb-4 space-y-2">
                  <li><strong>Active Accounts:</strong> We retain your account information, notes, and related
                  data for as long as your account remains active or as needed to provide the Service.</li>
                  <li><strong>Deleted Accounts:</strong> When you delete your account, or request deletion, we
                  will delete or anonymize your account data and User Content within a reasonable period, except
                  where retention is required or permitted by law, security, fraud prevention, backups,
                  legitimate operational needs, or dispute resolution.</li>
                  <li><strong>Backups and Logs:</strong> Residual copies may remain in backups, logs, or technical
                  records for a limited period before being overwritten or deleted according to our retention
                  practices. Server logs and analytics data may be retained for generally up to 90 days for
                  security and service improvement purposes.</li>
                  <li><strong>Legal Obligations:</strong> We may retain certain information for longer periods if
                  required by law or for legitimate business purposes (such as fraud prevention).</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
                  8. Your Privacy Rights
                </h2>
                <p className="text-gray-700 dark:text-gray-300 mb-4">
                  Depending on your location, you may have certain rights regarding your personal information.
                  Under the General Data Protection Regulation (GDPR) and other applicable laws, you have the
                  following rights:
                </p>

                <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-3 mt-6">
                  8.1 Right to Access
                </h3>
                <p className="text-gray-700 dark:text-gray-300 mb-4">
                  You have the right to request a copy of the personal information we hold about you.
                  <strong> Example:</strong> You can request a file containing all your account details, notes,
                  and usage history.
                </p>

                <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-3 mt-6">
                  8.2 Right to Rectification
                </h3>
                <p className="text-gray-700 dark:text-gray-300 mb-4">
                  You have the right to request that we correct any information you believe is inaccurate or
                  incomplete. <strong>Example:</strong> If your email address or name in your profile is incorrect,
                  you can update it in your account settings or contact us to make the correction.
                </p>

                <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-3 mt-6">
                  8.3 Right to Erasure (Right to be Forgotten)
                </h3>
                <p className="text-gray-700 dark:text-gray-300 mb-4">
                  You have the right to request that we delete your personal information under certain circumstances.
                  <strong> Example:</strong> You can request deletion of your account and personal data. We will
                  delete or anonymize applicable data within a reasonable period, subject to legal, security,
                  backup, and operational retention requirements.
                </p>

                <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-3 mt-6">
                  8.4 Right to Data Portability
                </h3>
                <p className="text-gray-700 dark:text-gray-300 mb-4">
                  You have the right to request that we transfer the data we have collected to another organization,
                  or directly to you, in a structured, commonly used, and machine-readable format.
                  <strong> Example:</strong> You can request a copy or export of your personal data in a
                  structured, commonly used, and machine-readable format where technically available and
                  required by applicable law.
                </p>

                <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-3 mt-6">
                  8.5 Right to Restrict Processing
                </h3>
                <p className="text-gray-700 dark:text-gray-300 mb-4">
                  You have the right to request that we restrict the processing of your personal information under
                  certain circumstances. <strong>Example:</strong> If you contest the accuracy of your data, you can
                  request that we restrict processing while we verify the information.
                </p>

                <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-3 mt-6">
                  8.6 Right to Object
                </h3>
                <p className="text-gray-700 dark:text-gray-300 mb-4">
                  You have the right to object to our processing of your personal information under certain circumstances.
                  <strong> Example:</strong> You can opt-out of receiving marketing emails by clicking the "unsubscribe"
                  link in any marketing email we send you.
                </p>

                <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-3 mt-6">
                  8.7 Right to Withdraw Consent
                </h3>
                <p className="text-gray-700 dark:text-gray-300 mb-4">
                  If we are processing your personal information based on your consent, you have the right to withdraw
                  that consent at any time. <strong>Example:</strong> You may withdraw consent where processing is
                  based on consent, for example by changing browser settings, using available cookie controls, or
                  contacting us.
                </p>

                <p className="text-gray-700 dark:text-gray-300 mt-6">
                  To exercise any of these rights, please contact us at admin@memoslate.com. We will respond to your
                  request within 30 days.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
                  9. Marketing Communications
                </h2>
                <p className="text-gray-700 dark:text-gray-300 mb-4">
                  We may send you emails about new features, products, services, or special offers. You have the
                  right to opt-out of receiving marketing communications at any time:
                </p>
                <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 mb-4 space-y-2">
                  <li>Click the "unsubscribe" link in any marketing email we send you, if available</li>
                  <li>Contact us at admin@memoslate.com to be removed from our marketing list</li>
                </ul>
                <p className="text-gray-700 dark:text-gray-300">
                  Please note that even if you opt-out of marketing communications, we will still send you
                  transactional and service-related emails (such as password reset emails, security alerts,
                  and account notifications).
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
                  10. Children's Privacy
                </h2>
                <p className="text-gray-700 dark:text-gray-300 mb-4">
                  Our Service is not intended for children under the age of 18. We do not knowingly collect
                  personal information from children under 18. If you are a parent or guardian and you become
                  aware that your child has provided us with personal information, please contact us at
                  admin@memoslate.com.
                </p>
                <p className="text-gray-700 dark:text-gray-300">
                  If we become aware that we have collected personal information from children under 18 without
                  verification of parental consent, we will take steps to remove that information from our servers.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
                  11. International Data Transfers
                </h2>
                <p className="text-gray-700 dark:text-gray-300 mb-4">
                  Your information, including personal data, may be transferred to and maintained on servers
                  located in Germany. If you are located outside Germany and choose to provide information to
                  us, please note that we transfer the data to Germany and process it there.
                </p>
                <p className="text-gray-700 dark:text-gray-300 mb-4">
                  Some third-party service providers, such as analytics, error monitoring, email, or payment
                  providers, may process data in other countries. Where required, we rely on appropriate
                  safeguards under applicable data protection laws.
                </p>
                <p className="text-gray-700 dark:text-gray-300">
                  We will take all steps reasonably necessary to ensure that your data is treated securely and
                  in accordance with this Privacy Policy and applicable data protection laws, including the GDPR.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
                  12. Changes to This Privacy Policy
                </h2>
                <p className="text-gray-700 dark:text-gray-300 mb-4">
                  We may update our Privacy Policy from time to time. We will notify you of any changes by:
                </p>
                <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 mb-4 space-y-2">
                  <li>Posting the new Privacy Policy on this page</li>
                  <li>Updating the "Last Updated" date at the top of this Privacy Policy</li>
                  <li>Sending you an email notification for significant changes</li>
                </ul>
                <p className="text-gray-700 dark:text-gray-300">
                  We encourage you to review this Privacy Policy periodically for any changes. Changes to this
                  Privacy Policy are effective when they are posted on this page.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
                  13. Contact Us
                </h2>
                <p className="text-gray-700 dark:text-gray-300 mb-4">
                  If you have any questions about this Privacy Policy, our data practices, or if you wish to
                  exercise your privacy rights, please contact us:
                </p>
                <div className="bg-gray-100 dark:bg-gray-800 rounded-lg p-4">
                  <p className="text-gray-800 dark:text-gray-200 mb-2">
                    <strong>Email:</strong> admin@memoslate.com
                  </p>
                  <p className="text-gray-800 dark:text-gray-200">
                    <strong>Location:</strong> Vilnius, Lithuania
                  </p>
                </div>
                <p className="text-gray-700 dark:text-gray-300 mt-4">
                  We will do our best to respond to all legitimate requests within 30 days.
                </p>
              </section>

              <div className="border-t border-gray-300 dark:border-gray-700 pt-8 mt-12">
                <p className="text-sm text-gray-600 dark:text-gray-400 italic">
                  By using MemoSlate, you acknowledge that you have read, understood, and agree to this
                  Privacy Policy and our Terms and Conditions.
                </p>
              </div>
            </div>
          </div>
        </LandingContainer>
      </main>
    </>
  );
}

Privacy.layout = (page: React.ReactNode) => <PublicLayout children={page} />;
export default Privacy;

